import React, { useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useServeFileById } from '@/hooks/upload/use-file';
import {
  ImagePreview,
  PdfPreview,
  DocumentPreview,
  SpreadsheetPreview,
  PresentationPreview,
  AudioPreview,
  VideoPreview
} from './previews';
import { useTranslation } from 'react-i18next';
import { useFormResponseStore } from '@/store';

interface AddDocumentProps {
  question: any;
  sectionId: string;
  sectionName: string;
}

// Utility function to determine document type from file extension
const getDocumentTypeFromExtension = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return 'image';
    case 'pdf':
      return 'pdf';
    case 'doc':
    case 'docx':
    case 'txt':
    case 'rtf':
      return 'document';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return 'spreadsheet';
    case 'ppt':
    case 'pptx':
      return 'presentation';
    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'aac':
    case 'flac':
      return 'audio';
    case 'mp4':
    case 'webm':
    case 'avi':
    case 'mov':
      return 'video';
    default:
      return 'document'; // Default fallback
  }
};

export const AddDocumentAnswer: React.FC<AddDocumentProps> = ({ question, sectionId, sectionName }) => {
  const { setResponse } = useFormResponseStore();
  const {
    data: fileUrl,
    isLoading: isLoadingFile,
    error: fileError
  } = useServeFileById(question.document || '');
  const { t } = useTranslation();

  useEffect(() => {
    if (question.document) {
      setResponse(
        sectionId,
        sectionName,
        question.id,
        question.type,
        question.label || '',
        {
          document: question.document,
          documentType: question.documentType
        }
      );
    }
  }, [question.id, question.type, question.label, question.document, question.documentType, sectionId, sectionName, setResponse]);

  const renderFilePreview = () => {
    if (isLoadingFile) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <Icon icon="mdi:loading" className="text-4xl text-gray-400 animate-spin" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Loading document...</p>
            </div>
          </div>
        </div>
      );
    }

    if (fileError || !fileUrl) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <Icon icon="mdi:file-alert" className="text-4xl text-red-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Document not found</p>
              <p className="text-xs text-gray-500">Unable to load the document</p>
            </div>
          </div>
        </div>
      );
    }

    const fileName = question.document || 'Unknown file';
    const documentType = question.documentType || getDocumentTypeFromExtension(fileName);
    const previewProps = { fileUrl, fileName };

    switch (documentType) {
      case 'image':
        return <ImagePreview {...previewProps} />;
      case 'pdf':
        return <PdfPreview {...previewProps} />;
      case 'document':
        return <DocumentPreview {...previewProps} />;
      case 'spreadsheet':
        return <SpreadsheetPreview {...previewProps} />;
      case 'presentation':
        return <PresentationPreview {...previewProps} />;
      case 'audio':
        return <AudioPreview {...previewProps} />;
      case 'video':
        return <VideoPreview {...previewProps} />;
      default:
        return (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
            <div className="flex items-center space-x-3">
              <Icon icon="mdi:file" className="text-4xl text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>
                  {fileName}
                </p>
                <p className="text-xs text-gray-500">Document</p>
              </div>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors"
              >
                {t("formResponses.downloadFile")}
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="mb-4">
      {renderFilePreview()}
    </div>
  );
};
